import Vue from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import vuetify from './plugins/vuetify'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import 'material-design-icons-iconfont/dist/material-design-icons.css'
import storage from 'vue-localstorage'
import VueMatomo from 'vue-matomo'
import axios from 'axios'

import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCodeBranch, faPuzzlePiece } from '@fortawesome/free-solid-svg-icons'
import { faVuejs, faNodeJs, faMicrosoft, faWordpress, faPython, faAndroid, faApple, faGolang, faJava, faPhp, faAngular, faReact, faJs } from '@fortawesome/free-brands-svg-icons'

import router from './router'

library.add(
  faCodeBranch,
  faVuejs,
  faNodeJs,
  faMicrosoft,
  faWordpress,
  faPuzzlePiece,
  faPython,
  faAndroid,
  faApple,
  faGolang,
  faJava,
  faPhp,
  faAngular,
  faReact,
  faJs
)

Vue.component('font-awesome-icon', FontAwesomeIcon)

Vue.config.productionTip = false

console.log('embrapa.io/manager@' + process.env.VUE_APP_STAGE)
console.log('Version: ' + process.env.VUE_APP_VERSION)

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  release: 'embrapa.io@' + process.env.VUE_APP_VERSION.split('-')[0],
  environment: process.env.VUE_APP_STAGE,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', 'manager.embrapa.io', /^\//]
    })
  ],
  tracesSampleRate: 1.0
})

Vue.use(storage, {
  name: '',
  bind: true
})

const orientation = {
  portrait: false,
  landscape: false
}

const changeOrientation = () => {
  orientation.portrait = window.screen.orientation.type === 'portrait-primary'
  orientation.landscape = window.screen.orientation.type === 'landscape-primary'
}

changeOrientation()

window.addEventListener(
  'orientationchange',
  changeOrientation
)

Vue.prototype.$orientation = orientation

Vue.use(VueMatomo, {
  host: 'https://hit.embrapa.io',
  siteId: process.env.VUE_APP_MATOMO_SITE_ID,
  router,
  preInitActions: [
    ['setCustomDimension', 1, process.env.VUE_APP_STAGE],
    ['setCustomDimension', 2, process.env.VUE_APP_VERSION]
  ]
})

// Interceptor global para tratar token expirado (401)
axios.interceptors.response.use(
  response => response,
  error => {
    // Log detalhado para debug
    if (error.response) {
      console.log('[Axios Interceptor] Error response:', {
        status: error.response.status,
        url: error.config?.url,
        method: error.config?.method
      })
    }

    // Apenas 401 (Unauthorized) deve causar logout
    // 403 (Forbidden) significa que o usuário está autenticado mas sem permissão
    if (error.response && error.response.status === 401) {
      console.warn('[Axios Interceptor] 401 Unauthorized - Token expirado ou inválido')

      // Token expirado ou inválido - limpa localStorage e redireciona para login
      const storage = window.localStorage
      storage.removeItem('user')
      storage.removeItem('reliable')
      storage.removeItem('email')

      // Redireciona para login
      if (router.currentRoute.path !== '/') {
        console.log('[Axios Interceptor] Redirecting to login')
        router.push('/')
      }
    } else if (error.response && error.response.status === 403) {
      console.warn('[Axios Interceptor] 403 Forbidden - Usuário autenticado mas sem permissão:', error.config?.url)
      // NÃO remove token nem redireciona - apenas loga o erro
    }

    return Promise.reject(error)
  }
)

// Valida JWT no localStorage ao carregar a aplicação
// Remove tokens antigos sem campo 'exp' (force re-login)
const validateStoredToken = () => {
  try {
    const storage = window.localStorage
    const userStr = storage.getItem('user')

    if (userStr) {
      const user = JSON.parse(userStr)

      if (user && user.token) {
        // Decodifica JWT (formato: header.payload.signature)
        const parts = user.token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))

          // Verifica se tem campo 'exp' (expiração)
          if (!payload.exp) {
            console.warn('[JWT] Token antigo sem expiração detectado. Limpando localStorage...')
            storage.removeItem('user')
            storage.removeItem('reliable')
            storage.removeItem('email')
            return true // Indica que limpou
          } else {
            // Verifica se token já expirou
            const now = Math.floor(Date.now() / 1000)
            if (payload.exp < now) {
              console.warn('[JWT] Token expirado detectado. Limpando localStorage...')
              storage.removeItem('user')
              storage.removeItem('reliable')
              storage.removeItem('email')
              return true // Indica que limpou
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('[JWT] Erro ao validar token:', error)
    // Em caso de erro ao decodificar, limpa localStorage por segurança
    const storage = window.localStorage
    storage.removeItem('user')
    storage.removeItem('reliable')
    storage.removeItem('email')
    return true // Indica que limpou
  }
  return false // Não limpou nada
}

// Executa validação antes de montar a aplicação
validateStoredToken()

// Router guard para garantir que após Vue montar, usuário sem token válido fique no login
router.beforeEach((to, from, next) => {
  const storage = window.localStorage
  const userStr = storage.getItem('user')

  console.log('[Router Guard] Navigation attempt:', { from: from.path, to: to.path })
  console.log('[Router Guard] User in localStorage:', userStr ? 'exists' : 'missing')

  // Se está indo para login, permite
  if (to.path === '/') {
    console.log('[Router Guard] Going to login, allowing')
    next()
    return
  }

  // Se não tem user no localStorage, redireciona para login
  if (!userStr) {
    console.log('[Router Guard] No user found, redirecting to login')
    next('/')
    return
  }

  try {
    const user = JSON.parse(userStr)

    // Verifica se tem token
    if (!user || !user.token) {
      console.log('[Router Guard] User exists but no token, redirecting to login')
      next('/')
      return
    }

    console.log('[Router Guard] Token found, validating...')

    // Valida token
    const parts = user.token.split('.')
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]))

      console.log('[Router Guard] Token payload:', { exp: payload.exp, now: Math.floor(Date.now() / 1000), valid: payload.exp > Math.floor(Date.now() / 1000) })

      // Token sem exp ou expirado
      if (!payload.exp || payload.exp < Math.floor(Date.now() / 1000)) {
        console.log('[Router Guard] Token expired, redirecting to login')
        storage.removeItem('user')
        storage.removeItem('reliable')
        storage.removeItem('email')
        next('/')
        return
      }
    }

    // Token válido, permite navegação
    console.log('[Router Guard] Token valid, allowing navigation to', to.path)
    next()
  } catch (error) {
    // Erro ao validar, limpa e redireciona
    console.error('[Router Guard] Error validating token:', error)
    storage.removeItem('user')
    storage.removeItem('reliable')
    storage.removeItem('email')
    next('/')
  }
})

new Vue({
  router,
  vuetify,
  render: h => h(App)
}).$mount('#app')
